import { getCloudinarySignature, attachListingVideo } from './api';

const DEFAULT_CONSTRAINTS = {
  max_duration_sec: 60,
  min_duration_sec: 3,
  max_bytes: 100 * 1024 * 1024,
  allowed_formats: ['mp4', 'mov', 'webm', 'm4v'],
};

export const probeVideoDuration = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement('video');
  v.preload = 'metadata';
  v.onloadedmetadata = () => {
    URL.revokeObjectURL(url);
    resolve(isFinite(v.duration) ? v.duration : 0);
  };
  v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
  v.src = url;
});

export const validateVideoFile = (file, constraints = DEFAULT_CONSTRAINTS, lang = 'fr') => {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!(constraints.allowed_formats || DEFAULT_CONSTRAINTS.allowed_formats).includes(ext)) {
    return lang === 'fr'
      ? `Format non supporté. Acceptés : ${constraints.allowed_formats.join(', ')}`
      : `Format not supported. Accepted: ${constraints.allowed_formats.join(', ')}`;
  }
  if (file.size > (constraints.max_bytes || DEFAULT_CONSTRAINTS.max_bytes)) {
    const max = Math.round((constraints.max_bytes || DEFAULT_CONSTRAINTS.max_bytes) / (1024 * 1024));
    return lang === 'fr' ? `Fichier trop volumineux (max ${max} MB)` : `File too large (max ${max} MB)`;
  }
  return null;
};

/**
 * Upload a video file to Cloudinary then attach to a listing.
 * @param {File} file
 * @param {string} listingId
 * @param {(progress:number)=>void} onProgress
 * @param {string} lang
 * @returns {Promise<{video_url, video_thumbnail, ...}>} updated listing data
 */
export const uploadVideoToListing = async (file, listingId, onProgress, lang = 'fr') => {
  // 1) signature
  const sigRes = await getCloudinarySignature(listingId);
  const sig = sigRes.data;
  const constraints = sig.constraints || DEFAULT_CONSTRAINTS;

  // 2) client-side validation
  const err = validateVideoFile(file, constraints, lang);
  if (err) throw new Error(err);

  const duration = await probeVideoDuration(file);
  const maxDur = constraints.max_duration_sec || DEFAULT_CONSTRAINTS.max_duration_sec;
  if (duration && duration > maxDur) {
    throw new Error(
      lang === 'fr'
        ? `Vidéo trop longue (${Math.round(duration)}s). Maximum ${maxDur}s.`
        : `Video too long. Maximum ${maxDur}s.`
    );
  }

  // 3) direct upload to Cloudinary with progress
  const form = new FormData();
  form.append('file', file);
  form.append('api_key', sig.api_key);
  form.append('timestamp', sig.timestamp);
  form.append('signature', sig.signature);
  form.append('folder', sig.folder);
  form.append('eager', sig.eager);
  form.append('eager_async', 'true');

  const uploaded = await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${sig.cloud_name}/${sig.resource_type}/upload`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 95));
    };
    xhr.onload = () => {
      try {
        const r = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && r.public_id) resolve(r);
        else reject(new Error(r?.error?.message || `Upload échoué (HTTP ${xhr.status})`));
      } catch (err) {
        reject(new Error(lang === 'fr' ? 'Réponse invalide de Cloudinary' : 'Invalid Cloudinary response'));
      }
    };
    xhr.onerror = () => reject(new Error(lang === 'fr' ? "Erreur réseau pendant l'envoi" : 'Network error during upload'));
    xhr.send(form);
  });

  onProgress?.(97);

  // 4) attach to listing
  const attach = await attachListingVideo(listingId, {
    public_id: uploaded.public_id,
    secure_url: uploaded.secure_url,
    duration: uploaded.duration,
    format: uploaded.format,
    bytes: uploaded.bytes,
    width: uploaded.width,
    height: uploaded.height,
  });

  onProgress?.(100);
  return attach.data;
};
